import { isNonProductionHost } from "./environment";

interface Config {
  stripePublishableKey: string;
  apiBase: string;
  googleApiKey: string;
  cleverClientId: string;
  cleverRedirectUri: string;
  cleverSubscribeRedirectUri: string;
}

declare global {
  interface Window {
    ENV?: {
      REACT_APP_STRIPE_PUBLISHABLE_KEY: string;
      REACT_APP_API_BASE: string;
      REACT_APP_GOOGLE_API_KEY: string;
      REACT_APP_CLEVER_CLIENT_ID: string;
      REACT_APP_CLEVER_REDIRECT_URI: string;
      REACT_APP_CLEVER_SUBSCRIBE_REDIRECT_URI: string;
      REACT_APP_RECAPTCHA_SITE_KEY: string;
      REACT_APP_GA_MEASUREMENT_ID: string;
      REACT_APP_MAILCHIMP_URL: string;
      REACT_APP_COLLEGE_FINDER_ENABLED?: string;
      REACT_APP_COLLEGE_CHAT_SEARCH_ENABLED?: string;
    };
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * reCAPTCHA v3 site key: prefer runtime `config.js` (Docker/envsubst), then CRA build-time env.
 */
export function getRecaptchaSiteKey(): string {
  const runtime = window.ENV?.REACT_APP_RECAPTCHA_SITE_KEY?.trim();
  if (runtime) {
    return runtime;
  }
  return process.env.REACT_APP_RECAPTCHA_SITE_KEY?.trim() ?? "";
}

/**
 * Google Analytics 4 measurement ID: prefer runtime `config.js`, then CRA build-time env.
 * Empty string means GA is disabled for this environment.
 */
export function getGaMeasurementId(): string {
  const runtime = window.ENV?.REACT_APP_GA_MEASUREMENT_ID?.trim();
  if (runtime) {
    return runtime;
  }
  return process.env.REACT_APP_GA_MEASUREMENT_ID?.trim() ?? "";
}

/**
 * Mailchimp newsletter form action URL: prefer runtime `config.js`, then CRA build-time env.
 * Empty string means the newsletter form has no configured destination yet.
 */
export function getMailchimpUrl(): string {
  const runtime = window.ENV?.REACT_APP_MAILCHIMP_URL?.trim();
  if (runtime && !runtime.includes("${")) {
    return runtime;
  }
  return process.env.REACT_APP_MAILCHIMP_URL?.trim() ?? "";
}

/**
 * College finder wizard. An explicit true/false env value wins.
 * When unset, the finder is on for local and staging and off on production.
 */
export function isCollegeFinderEnabled(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : "",
): boolean {
  const configured = parseBooleanFlag(
    window.ENV?.REACT_APP_COLLEGE_FINDER_ENABLED ?? process.env.REACT_APP_COLLEGE_FINDER_ENABLED,
  );
  if (configured !== undefined) {
    return configured;
  }
  return isNonProductionHost(hostname);
}

/**
 * Assist-style chat search on college planning. An explicit true/false env value wins.
 * When unset, chat search is on for local and staging and off on production.
 */
export function isCollegeChatSearchEnabled(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : "",
): boolean {
  const configured = parseBooleanFlag(
    window.ENV?.REACT_APP_COLLEGE_CHAT_SEARCH_ENABLED ?? process.env.REACT_APP_COLLEGE_CHAT_SEARCH_ENABLED,
  );
  if (configured !== undefined) {
    return configured;
  }
  return isNonProductionHost(hostname);
}

/** Staging and local can flip between wizard and chat search. Production cannot. */
export function isCollegeSearchCompareToggleVisible(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : "",
): boolean {
  return isNonProductionHost(hostname);
}

function parseBooleanFlag(value: string | undefined): boolean | undefined {
  if (value == null) {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === "" || normalized.includes("${")) {
    return undefined;
  }
  if (normalized === "true" || normalized === "1" || normalized === "yes") {
    return true;
  }
  if (normalized === "false" || normalized === "0" || normalized === "no") {
    return false;
  }
  return undefined;
}

export const getConfig = (): Config => {
  return {
    stripePublishableKey: window.ENV?.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
    apiBase: window.ENV?.REACT_APP_API_BASE || 'localhost',
    googleApiKey: window.ENV?.REACT_APP_GOOGLE_API_KEY || '',
    cleverClientId: window.ENV?.REACT_APP_CLEVER_CLIENT_ID || '',
    cleverRedirectUri: window.ENV?.REACT_APP_CLEVER_REDIRECT_URI || '',
    cleverSubscribeRedirectUri: window.ENV?.REACT_APP_CLEVER_SUBSCRIBE_REDIRECT_URI || '',
  };
};

